import { useState } from 'react';
import { Box, Chip, Stack, Alert, TextField, IconButton, Tooltip, CircularProgress } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { toast } from 'react-toastify';
import SectionHeading from './SectionHeading';
import { apiPut } from '../../../../api/client';

const NO_ALLERGIES = 'No known allergies';
const UNKNOWN = 'Unknown';

// A single value matching one of these reads as an affirmative "negative"
// assertion (NKDA / unknown), not a recorded allergy — so it shows as a
// neutral completed state rather than an orange allergy chip, and is kept
// out of the banner's allergy alert strip.
const NEGATIVE_RE = /^(no known allergies|nkda|nka|no allergies|none|unknown|n\/a)$/i;

// Allergies are stored on the patient's medical-history record as a single
// comma-separated string. Parse to a clean list for display.
function parseAllergies(value) {
    if (Array.isArray(value)) {
        return value.map((a) => (typeof a === 'string' ? a : a?.name)).filter(Boolean);
    }
    if (typeof value === 'string') {
        return value.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
}

export default function AllergiesTab({ patient, onReload, disabled }) {
    const pId = patient?._id;
    const current = parseAllergies(patient?.allergies ?? patient?.medicalHistory?.allergies);
    const isNegative = current.length === 1 && NEGATIVE_RE.test(current[0]);
    const [input, setInput] = useState('');
    const [saving, setSaving] = useState(false);

    // Persist the full list (the server stores it as one comma-separated
    // string on the patient record) and reload so the alert strip updates.
    const persist = async (next) => {
        if (!pId) return;
        setSaving(true);
        try {
            await apiPut(`api/patient-allergies/${pId}`, { allergies: next.join(', ') });
            await onReload?.();
        } catch (err) {
            toast.error('Could not update allergies. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const addAllergy = async () => {
        const v = input.trim();
        if (!v || saving) return;
        // A real allergy supersedes any "none"/"unknown" assertion.
        const base = isNegative ? [] : current;
        if (base.some((a) => a.toLowerCase() === v.toLowerCase())) { setInput(''); return; }
        setInput('');
        await persist([...base, v]);
    };

    const removeAllergy = async (name) => {
        if (saving) return;
        await persist(current.filter((a) => a !== name));
    };

    const setSentinel = async (val) => {
        if (saving) return;
        // Toggle off if the same assertion is already selected.
        await persist(isNegative && current[0] === val ? [] : [val]);
    };

    const negChipSx = (active) => (active ? {
        bgcolor: 'rgba(46,125,50,0.12)',
        color: 'success.dark',
        border: '1px solid rgba(46,125,50,0.4)',
        fontWeight: 600,
        '& .MuiChip-icon': { color: 'success.dark' },
    } : {
        borderColor: 'rgba(5,47,93,0.30)',
        color: '#052F5D',
        fontWeight: 600,
    });

    return (
        <Box>
            <SectionHeading
                title="Allergies"
                hint="Saved to the patient record — they appear in the allergy alert strip above the encounter on this and every future visit. Use the quick options if the patient has none or it's unknown."
            />

            {!disabled && (
                <>
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                        {[NO_ALLERGIES, UNKNOWN].map((val) => {
                            const active = isNegative && current[0] === val;
                            return (
                                <Chip
                                    key={val}
                                    clickable
                                    onClick={() => setSentinel(val)}
                                    disabled={saving}
                                    icon={active ? <CheckRoundedIcon /> : undefined}
                                    label={val}
                                    variant={active ? 'filled' : 'outlined'}
                                    sx={negChipSx(active)}
                                />
                            );
                        })}
                    </Stack>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, maxWidth: 440 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Add an allergy (e.g. Penicillin)"
                            value={input}
                            disabled={saving}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAllergy(); } }}
                        />
                        <Tooltip title="Add allergy" arrow>
                            <span>
                                <IconButton onClick={addAllergy} disabled={saving || !input.trim()} color="primary">
                                    {saving ? <CircularProgress size={20} /> : <AddRoundedIcon />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </>
            )}

            {isNegative ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                        label={current[0]}
                        onDelete={disabled || saving ? undefined : () => persist([])}
                        sx={{
                            bgcolor: 'rgba(46,125,50,0.10)',
                            color: 'success.dark',
                            fontWeight: 500,
                            '& .MuiChip-deleteIcon': { color: 'rgba(46,125,50,0.55)' },
                            '&:hover .MuiChip-deleteIcon': { color: 'success.dark' },
                        }}
                    />
                </Stack>
            ) : current.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>
                    No allergies recorded for this patient.
                </Alert>
            ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {current.map((a, i) => (
                        <Chip
                            key={`${a}-${i}`}
                            label={a}
                            onDelete={disabled || saving ? undefined : () => removeAllergy(a)}
                            sx={{
                                bgcolor: '#FFF6E5',
                                color: '#5C3A00',
                                border: '1px solid #F5C26B',
                                fontWeight: 500,
                            }}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
}
